import { useState, useEffect } from 'react';
import { Gauge, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getRateLimits, updateRateLimit, type RateLimitConfig } from '@/lib/adminApi';

const ENDPOINT_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  auth: { label: 'Authentication', description: 'Login, logout, password reset' },
  control: { label: 'Control Actions', description: 'Device commands, bulk operations' },
  query: { label: 'Data Queries', description: 'API reads, dashboard loads' },
  default: { label: 'Default', description: 'All other endpoints' },
};

export function RateLimitPanel() {
  const [rateLimits, setRateLimits] = useState<RateLimitConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<RateLimitConfig>>>({});

  useEffect(() => {
    loadRateLimits();
  }, []);

  const loadRateLimits = async () => {
    try {
      setIsLoading(true);
      const data = await getRateLimits();
      setRateLimits(data);
    } catch (error: unknown) {
      console.error('Failed to load rate limits:', error);
      toast.error('Failed to load rate limit configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (endpointType: string, field: keyof RateLimitConfig, value: number | boolean) => {
    setPendingChanges(prev => ({
      ...prev,
      [endpointType]: {
        ...prev[endpointType],
        [field]: value,
      },
    }));
  };

  const handleSave = async (endpointType: string) => {
    const changes = pendingChanges[endpointType];
    if (!changes) return;

    try {
      const updated = await updateRateLimit(endpointType, changes);
      setRateLimits(prev => prev.map(rl =>
        rl.endpoint_type === endpointType ? updated : rl
      ));
      setPendingChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[endpointType];
        return newChanges;
      });
      toast.success(`Rate limit for ${ENDPOINT_TYPE_LABELS[endpointType]?.label || endpointType} updated`);
    } catch (error: unknown) {
      console.error('Failed to update rate limit:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update rate limit');
    }
  };

  const getCurrentValue = <T extends keyof RateLimitConfig>(
    config: RateLimitConfig,
    field: T
  ): RateLimitConfig[T] => {
    const pending = pendingChanges[config.endpoint_type];
    if (pending && field in pending) {
      return pending[field] as RateLimitConfig[T];
    }
    return config[field];
  };

  const hasChanges = (endpointType: string) => {
    return endpointType in pendingChanges && Object.keys(pendingChanges[endpointType]).length > 0;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-12 bg-muted rounded" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Gauge className="w-4 h-4 text-yellow-400" />
        <h3 className="text-sm font-semibold text-white">Rate Limiting</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Control request rates per IP address for different endpoint types.
      </p>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader className="bg-background">
            <TableRow className="border-border hover:bg-background">
              <TableHead className="text-muted-foreground">Endpoint Type</TableHead>
              <TableHead className="text-muted-foreground text-center w-24">Window (sec)</TableHead>
              <TableHead className="text-muted-foreground text-center w-24">Max Requests</TableHead>
              <TableHead className="text-muted-foreground text-center w-20">Enabled</TableHead>
              <TableHead className="text-muted-foreground text-right w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rateLimits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No rate limit configuration found. Run migrations to set up defaults.
                </TableCell>
              </TableRow>
            ) : (
              rateLimits.map((config) => {
                const typeInfo = ENDPOINT_TYPE_LABELS[config.endpoint_type] || {
                  label: config.endpoint_type,
                  description: 'Custom endpoint type',
                };
                return (
                  <TableRow key={config.endpoint_type} className="border-border hover:bg-muted/50">
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{typeInfo.label}</p>
                        <p className="text-xs text-muted-foreground">{typeInfo.description}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        value={getCurrentValue(config, 'window_seconds')}
                        onChange={(e) => handleChange(config.endpoint_type, 'window_seconds', parseInt(e.target.value) || 60)}
                        className="bg-background border-border h-8 w-20 text-center mx-auto"
                        min={1}
                        max={3600}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        value={getCurrentValue(config, 'max_requests')}
                        onChange={(e) => handleChange(config.endpoint_type, 'max_requests', parseInt(e.target.value) || 1)}
                        className="bg-background border-border h-8 w-20 text-center mx-auto"
                        min={1}
                        max={1000}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">
                        <Switch
                          checked={getCurrentValue(config, 'enabled') as boolean}
                          onCheckedChange={(checked) => handleChange(config.endpoint_type, 'enabled', checked)}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {hasChanges(config.endpoint_type) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSave(config.endpoint_type)}
                          className="h-8 text-[#8FB560] hover:text-[#7a9d51]"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
